import { type ReactElement, useState, useRef } from "react";
import { InputAdornment, type SxProps, TextField, Typography } from "@mui/material";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

interface NumberInputProps {
  id?: string;
  initialValue?: number;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  onChange?: (value: number) => void;
  size?: "small";
  sx?: SxProps;
  error?: boolean;
  disabled?: boolean;
}

export function NumberInput(props: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { id, initialValue, min, max, step = 1, unit, onChange, size, sx, error, disabled } = props;
  const [_value, _setValue] = useState(initialValue ?? 0);
  const setValue = (newValue: number) => {
    if (typeof min === "number" && newValue < min) {
      return;
    }
    if (typeof max === "number" && newValue > max) {
      return;
    }
    _setValue(newValue);
    onChange?.(newValue);
  };
  const value = props.value ?? _value;

  const stepperButtonSize = size === "small" ? "15px" : "20px";
  const stepperButtonStyle = {
    padding: 0,
    maxHeight: stepperButtonSize,
    maxWidth: stepperButtonSize,
    minHeight: stepperButtonSize,
    minWidth: stepperButtonSize,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    lineHeight: 1,
  };
  const stepperIconStyle = { fontSize: size === "small" ? "14px" : "18px" };

  let endAdornment: ReactElement = (
    <Column>
      <button
        aria-label="Wert erhöhen"
        onMouseDown={(event) => {
          event.preventDefault(); // ... do not steal focus
          inputRef.current?.focus(); // ... focus the text input field instead
        }}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setValue(value + step);
          return false;
        }}
        tabIndex={-1}
        style={stepperButtonStyle}
        disabled={disabled}
      >
        <ArrowDropUpIcon sx={stepperIconStyle} />
      </button>
      <button
        aria-label="Wert verringern"
        onMouseDown={(event) => {
          event.preventDefault(); // ... do not steal focus
          inputRef.current?.focus(); // ... focus the text input field instead
        }}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setValue(value - step);
          return false;
        }}
        tabIndex={-1}
        style={stepperButtonStyle}
        disabled={disabled}
      >
        <ArrowDropDownIcon sx={stepperIconStyle} />
      </button>
    </Column>
  );
  if (unit) {
    endAdornment = (
      <Row sx={{ alignItems: "center", gap: 1 }}>
        <Typography variant="body2" component="div" sx={{ color: "text.primary" }}>
          {unit}
        </Typography>
        {endAdornment}
      </Row>
    );
  }

  return (
    <TextField
      id={id}
      inputRef={inputRef}
      type="number"
      value={value}
      onChange={({ target: { value } }) => {
        const newValue = Number(value);
        setValue(newValue);
      }}
      size={size}
      sx={sx}
      error={error}
      InputProps={{
        endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
      }}
      disabled={disabled}
    />
  );
}
